import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import RejectOrderConfirmation from "../EditItem/RejectOrderConfirmation";
import TimedDishCard, { DishTiming } from "./TimedDishCard";
import {
  REJECTION_REASON_OPTIONS,
  validateRejectionReason,
} from "../../rejectionReasons";
import "./ItemWiseView.css";

const formatNumber = (value) =>
  new Intl.NumberFormat().format(Number.isFinite(Number(value)) ? Number(value) : 0);
const entryKey = (entry) =>
  String(entry?.key ?? `${entry?.orderId ?? ""}:${entry?.taskId ?? ""}`);
const statusOf = (entry) =>
  String(entry?.status || entry?.jobStatus || "").toUpperCase();
const isPending = (entry) => statusOf(entry) === "PENDING";
const isProcessing = (entry) =>
  ["IN_PROGRESS", "PROCESSING", "PREPARING"].includes(statusOf(entry));
const isStrictlyReadyEligible = (entry) =>
  String(entry?.jobStatus || entry?.status || "").toUpperCase() === "IN_PROGRESS" &&
  statusOf(entry) === "IN_PROGRESS" &&
  entry?.taskId !== undefined && entry?.taskId !== null && entry?.taskId !== "";
const quantityOf = (entries) =>
  entries.reduce((sum, entry) => sum + (Number(entry?.quantity) || 0), 0);
const orderCountOf = (entries) =>
  new Set(entries.map((entry) => entry?.orderId).filter(Boolean)).size;
const formatUpdatedAt = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).format(date);
};
const snapshotOf = (entries) =>
  JSON.stringify(entries.map((entry) => [
    entryKey(entry), entry?.orderId, entry?.taskId, entry?.quantity,
    entry?.status, entry?.jobStatus,
  ]));

export const ItemWorkloadStatus = ({ loading = false, error = "", lastUpdated = null, hasData = false }) => {
  const updatedLabel = formatUpdatedAt(lastUpdated);
  const refreshing = loading && hasData;
  return (
    <div className="item-workload-status" aria-live="polite">
      <span className={`item-workload-status__dot${refreshing ? " is-refreshing" : ""}${error ? " is-error" : ""}`} aria-hidden="true" />
      <span className="item-workload-status__label">{error ? "Workload unavailable" : loading ? (refreshing ? "Updating" : "Loading") : "Live"}</span>
      {updatedLabel && <span className="item-workload-status__updated">Updated {updatedLabel}</span>}
    </div>
  );
};

const ItemWiseView = ({
  groups = [],
  loading = false,
  error = "",
  onRetry,
  lastUpdated = null,
  actionBusy = false,
  onOpenOrder,
  onAccept,
  onReject,
  onReady,
  onReadyQuantity,
  aggregatedReady = false,
  allowAccept = true,
  allowReject = true,
}) => {
  const safeGroups = Array.isArray(groups) ? groups : [];
  const [confirmation, setConfirmation] = useState(null);
  const [quantityDrafts, setQuantityDrafts] = useState({});
  const [quantityVariants, setQuantityVariants] = useState({});
  const [rejectReason, setRejectReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [actionError, setActionError] = useState("");
  const [rejectError, setRejectError] = useState("");
  const [localBusy, setLocalBusy] = useState(false);
  const actionGuard = useRef(false);
  const activeEntries = useMemo(
    () => safeGroups.flatMap((group) => Array.isArray(group?.entries) ? group.entries : []),
    [safeGroups],
  );
  const totals = useMemo(() => ({
    portions: safeGroups.reduce((sum, group) => sum + (Number(group?.totalQty) || 0), 0),
    pending: activeEntries.filter(isPending).reduce((sum, entry) => sum + (Number(entry?.quantity) || 0), 0),
    processing: activeEntries.filter(isProcessing).reduce((sum, entry) => sum + (Number(entry?.quantity) || 0), 0),
    orders: orderCountOf(activeEntries),
  }), [safeGroups, activeEntries]);
  const busy = actionBusy || localBusy;

  const groupPanels = useMemo(() => safeGroups.map((group, index) => {
    const entries = Array.isArray(group?.entries) ? group.entries : [];
    const pending = entries.filter(isPending);
    const processing = entries.filter(isProcessing);
    return {
      group,
      key: String(group?.key ?? `${group?.name ?? "dish"}-${index}`),
      pending,
      processing,
    };
  }), [safeGroups]);

  const getCurrentSnapshot = (kind, key) => {
    const panel = groupPanels.find((candidate) => candidate.key === key);
    if (!panel) return "";
    return snapshotOf(kind === "accept" || kind.startsWith("reject") || kind.startsWith("pending-") ? panel.pending : panel.processing);
  };

  useEffect(() => {
    if (confirmation && confirmation.snapshot !== getCurrentSnapshot(confirmation.type, confirmation.groupKey)) {
      setConfirmation(null);
      setActionError("");
      setRejectError("");
    }
  }, [confirmation, groupPanels]);

  const cancelConfirmation = () => {
    setConfirmation(null);
    setActionError("");
    setRejectError("");
  };

  const confirmAction = async (callback, entries, extraArgs = [], successMessage = "") => {
    if (typeof callback !== "function" || actionGuard.current || busy || loading || error) return;
    actionGuard.current = true;
    setLocalBusy(true);
    setActionError("");
    try {
      await callback(entries, ...extraArgs);
      setConfirmation(null);
      setActionError("");
      if (successMessage === "quantity") {
          setQuantityDrafts((current) => ({ ...current, [confirmation.draftKey || confirmation.groupKey]: "" }));
      }
    } catch (caught) {
      const message = caught?.message || "That action could not be completed. Your draft is still here.";
      if (confirmation?.type?.startsWith("reject")) setRejectError(message);
      else setActionError(message);
    } finally {
      actionGuard.current = false;
      setLocalBusy(false);
    }
  };

  const openConfirmation = (type, groupKey, entries) => {
    setActionError("");
    setRejectError("");
    if (type === "reject") {
      setRejectReason("");
      setCustomReason("");
    }
    setConfirmation({
      type,
      groupKey,
      snapshot: snapshotOf(entries),
      entries: [...entries],
      quantity: null,
    });
  };

  const setQuantityReview = (panel, quantity, panelId = "processing", selectedEntries = panel.processing) => {
    setActionError("");
    setConfirmation({
      type: panelId === "pending" ? "pending-quantity" : "quantity",
      groupKey: panel.key,
      snapshot: snapshotOf(panelId === "pending" ? panel.pending : panel.processing),
      entries: [...selectedEntries],
      draftKey: `${panelId}:${panel.key}`,
      quantity,
    });
  };

  const rejectValidation = validateRejectionReason(rejectReason, customReason);
  const unavailable = Boolean(error) || (loading && !lastUpdated && safeGroups.length === 0);
  const count = (value) => unavailable ? "—" : formatNumber(value);
  const panels = [
    {
      id: "pending",
      title: "Pending / new orders",
      eyebrow: "Not started",
      className: "pending",
      entriesFor: (panel) => panel.pending,
    },
    {
      id: "processing",
      title: "Processing",
      eyebrow: "On the line",
      className: "processing",
      entriesFor: (panel) => panel.processing,
    },
  ];

  return (
    <section className="item-wise-view" aria-label="Consolidated kitchen workload">
      <div className="item-wise-summary" aria-label="Global workload summary">
        <div className="item-wise-total">
          <span className="item-wise-total-label">Portions on the pass</span>
          <strong>{count(totals.portions)}</strong>
          <span className="item-wise-total-foot">across {formatNumber(safeGroups.length)} {safeGroups.length === 1 ? "dish" : "dishes"}</span>
        </div>
        <div className="item-wise-summary-stat item-wise-summary-stat--prepare">
          <span className="item-wise-stat-mark" aria-hidden="true" />
          <span className="item-wise-stat-label">Pending</span>
          <strong>{count(totals.pending)}</strong>
          <span className="item-wise-stat-caption">portions to start</span>
        </div>
        <div className="item-wise-summary-stat item-wise-summary-stat--processing">
          <span className="item-wise-stat-mark" aria-hidden="true" />
          <span className="item-wise-stat-label">Processing</span>
          <strong>{count(totals.processing)}</strong>
          <span className="item-wise-stat-caption">portions in prep</span>
        </div>
        <div className="item-wise-summary-stat item-wise-summary-stat--orders">
          <span className="item-wise-stat-label">Contributing orders</span>
          <strong>{count(totals.orders)}</strong>
          <span className="item-wise-stat-caption">unique orders, all dishes</span>
        </div>
      </div>

      <p className="item-wise-api-note" role="note">
        Dish actions require their dedicated API; confirming never changes counts locally.
      </p>

      {error && (
        <div className="item-wise-message item-wise-error" role="alert">
          <div><strong>Workload couldn’t be loaded</strong><p>{error}</p></div>
          {typeof onRetry === "function" && <button type="button" className="item-wise-retry" onClick={onRetry}>Try again</button>}
        </div>
      )}

      {!error && loading && safeGroups.length === 0 && (
        <div className="item-wise-skeletons" aria-label="Loading dish counts" aria-busy="true">
          {[0, 1, 2, 3].map((item) => <div className="item-wise-skeleton" key={item}><span /><span /><span /></div>)}
        </div>
      )}

      {!error && !loading && safeGroups.length === 0 && (
        <div className="item-wise-message item-wise-empty">
          <span className="item-wise-empty-rule" aria-hidden="true" />
          <div><strong>Nothing on the pass right now</strong><p>Dish totals will appear here as live orders arrive.</p></div>
        </div>
      )}

      {!error && safeGroups.length > 0 && (
        <div className="item-wise-panels" aria-busy={loading}>
          {panels.map((panel, panelIndex) => {
            const visible = groupPanels.filter((groupPanel) => panel.entriesFor(groupPanel).length > 0);
            const panelQuantity = visible.reduce((sum, groupPanel) => sum + quantityOf(panel.entriesFor(groupPanel)), 0);
            const panelOrders = orderCountOf(visible.flatMap((groupPanel) => panel.entriesFor(groupPanel)));
            return (
              <section className={`item-wise-panel item-wise-panel--${panel.className}`} aria-labelledby={`item-wise-${panel.id}-title`} key={panel.id}>
                <header className="item-wise-panel-header">
                  <div className="item-wise-panel-heading">
                    <span className="item-wise-panel-mark" aria-hidden="true">{String(panelIndex + 1).padStart(2, "0")}</span>
                    <div><span className="item-wise-panel-eyebrow">{panel.eyebrow}</span><h2 id={`item-wise-${panel.id}-title`}>{panel.title}</h2></div>
                  </div>
                  <div className="item-wise-panel-totals">
                    <strong>{formatNumber(panelQuantity)}</strong><span>portions</span>
                    <i aria-hidden="true" />
                    <strong>{formatNumber(panelOrders)}</strong><span>{panelOrders === 1 ? "order" : "orders"}</span>
                  </div>
                </header>
                {visible.length === 0 ? (
                  <div className="item-wise-panel-empty"><span aria-hidden="true">—</span><p>{panel.id === "pending" ? "No new portions waiting." : "Nothing is being prepared right now."}</p></div>
                ) : (
                  <div className="item-wise-dish-list">
                    {visible.map((groupPanel, dishIndex) => {
                      const { group, key } = groupPanel;
                      const entries = panel.entriesFor(groupPanel);
                      const quantity = quantityOf(entries);
                      const orders = orderCountOf(entries);
                      const signature = snapshotOf(entries);
                      const confirming = confirmation?.groupKey === key;
                      const pendingConfirm = panel.id === "pending" && confirming && ["accept", "reject"].includes(confirmation?.type);
                      const readyType = "ready";
                      const fullReadyConfirm = confirming && confirmation?.type === readyType;
                      const quantityConfirm = confirming && confirmation?.type === "quantity";
                      const readyCallback = onReady;
                      const quantityCallback = onReadyQuantity;
                      const strictlyEligible = entries.length > 0 && entries.every(aggregatedReady
                        ? (entry) => entry.status === "IN_PROGRESS" && !!entry.variantId
                        : isStrictlyReadyEligible);
                      const draftKey = `${panel.id}:${key}`;
                      const variants = [...new Map(entries.filter((entry) => entry.variantId)
                        .map((entry) => [entry.variantId, entry.variant || entry.variantId])).entries()];
                      const needsVariant = variants.length > 1;
                      const quantityEntries = needsVariant ? entries.filter((entry) => entry.variantId === quantityVariants[draftKey]) : entries;
                      const quantityLimit = quantityOf(quantityEntries);
                      const draft = quantityDrafts[draftKey] ?? "";
                      const parsedQuantity = Number(draft);
                      const validQuantity = Number.isFinite(parsedQuantity) && Number.isInteger(parsedQuantity) && parsedQuantity > 0 && parsedQuantity <= quantityLimit;
                      const staleConfirmation = confirmation?.groupKey === key && confirmation.snapshot !== signature;
                      const variantSummary = [...new Set(entries.map((entry) => entry?.variant).filter(Boolean))];
                      const instructionCount = entries.filter((entry) => entry?.note || entry?.addons?.length).length;
                      // Total each add-on across every order line of this dish.
                      const addonSummary = [...entries.reduce((totals, entry) => {
                        (Array.isArray(entry?.addons) ? entry.addons : []).forEach((addon) => {
                          if (!addon?.name) return;
                          totals.set(addon.name, (totals.get(addon.name) || 0) + (Number(addon.quantity) || 1));
                        });
                        return totals;
                      }, new Map()).entries()];
                      return (
                        <TimedDishCard entries={entries} panelId={panel.id} key={`${panel.id}:${key}`}>
                          <div className="item-wise-dish-main">
                            <div className="item-wise-dish-identity">
                              <h3>{group?.name || "Unnamed dish"}</h3>
                              <span className="item-wise-dish-meta">
                                {variantSummary.length > 0 && <span className="item-wise-variants">{variantSummary.join(" · ")}</span>}
                                <span className="item-wise-order-count">{formatNumber(orders)} {orders === 1 ? "order" : "orders"}</span>
                              </span>
                              {addonSummary.length > 0 && (
                                <span className="item-wise-addons">
                                  Add-ons: {addonSummary.map(([name, count], addonIndex) => (
                                    <React.Fragment key={name}>
                                      {addonIndex > 0 && ", "}
                                      {name}{count > 1 && <> <strong>(x{formatNumber(count)})</strong></>}
                                    </React.Fragment>
                                  ))}
                                </span>
                              )}
                              <DishTiming />
                            </div>
                            <div className="item-wise-quantity-total"><strong>{formatNumber(quantity)}</strong><span>portions</span></div>
                            {panel.id === "pending" && !pendingConfirm && (
                              <div className="item-wise-quick-actions" aria-label={`Actions for ${group?.name || "dish"}`}>
                                <button type="button" className="item-wise-icon-action item-wise-icon-action--accept" aria-label={`Accept ${group?.name || "dish"} portions`} title={`Accept ${formatNumber(quantity)} portions`} disabled={busy || loading || !!error || !allowAccept || entries.length === 0} onClick={() => openConfirmation("accept", key, entries)}>
                                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4.5 4.5L19 7" /></svg>
                                </button>
                                {allowReject && <button type="button" className="item-wise-icon-action item-wise-icon-action--reject" aria-label={`Reject ${group?.name || "dish"} portions`} title={`Reject ${formatNumber(quantity)} portions`} disabled={busy || loading || !!error || entries.length === 0} onClick={() => openConfirmation("reject", key, entries)}>
                                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
                                </button>}
                              </div>
                            )}
                          </div>
                          {panel.id === "pending" && !allowReject && <p className="item-wise-config-note">Rejection disabled by kitchen settings.</p>}
                          {instructionCount > 0 && <p className="item-wise-instruction-cue">{instructionCount} {instructionCount === 1 ? "line has" : "lines have"} special instructions · review breakdown</p>}

                          {panel.id === "pending" ? (
                            <div className="item-wise-actions">
                              {pendingConfirm ? (
                                <div className="item-wise-confirm" role="group" aria-label={`Confirm ${confirmation.type} for ${group?.name}`}>
                                  <p>{confirmation.type === "accept" ? `Accept ${formatNumber(quantity)} portions from ${formatNumber(orders)} orders? Only this dish is affected; other dishes stay unchanged.` : `Reject these ${formatNumber(quantity)} portions from ${formatNumber(orders)} contributing orders? Only this dish is affected.`}</p>
                                  {confirmation.type === "accept" && typeof onAccept !== "function" && <span className="item-wise-api-warning">Accept API not connected</span>}
                                  {confirmation.type === "reject" && typeof onReject !== "function" && <span className="item-wise-api-warning">Reject API not connected</span>}
                                  {actionError && <p className="item-wise-inline-error" role="alert">{actionError}</p>}
                                  <button type="button" onClick={cancelConfirmation} disabled={busy}>Cancel</button>
                                  <button
                                    type="button"
                                    className="item-wise-confirm-primary"
                                    disabled={busy || loading || !!error || staleConfirmation || (confirmation.type === "accept" && (!allowAccept || typeof onAccept !== "function"))}
                                    onClick={() => confirmation.type === "accept"
                                      ? confirmAction(onAccept, confirmation.entries)
                                      : setConfirmation((current) => current ? { ...current, type: "reject-dialog" } : current)}
                                  >{confirmation.type === "accept"
                                      ? busy ? <><CircularProgress size={12} color="inherit" aria-label="Accepting items" /> Accepting…</> : "Confirm accept"
                                      : "Choose rejection reason"}</button>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          {panel.id === "processing" && (
                            <div className="item-wise-actions item-wise-processing-actions">
                              {!fullReadyConfirm ? (
                                <button type="button" className="item-wise-action item-wise-action--ready" disabled={busy || loading || !!error || !strictlyEligible || typeof readyCallback !== "function"} onClick={() => openConfirmation(readyType, key, entries)}>
                                  Mark all ready <span>{formatNumber(quantity)} portions · {formatNumber(orders)} orders</span>
                                </button>
                              ) : (
                                <div className="item-wise-confirm" role="group" aria-label={`Confirm all portions ready for ${group?.name}`}>
                                  <p>Mark all {formatNumber(quantity)} portions across {formatNumber(orders)} orders ready? Completion remains recorded under each respective order.</p>
                                  {typeof readyCallback !== "function" && <span className="item-wise-api-warning">Full-ready API not connected</span>}
                                  {actionError && <p className="item-wise-inline-error" role="alert">{actionError}</p>}
                                  <button type="button" onClick={cancelConfirmation} disabled={busy}>Cancel</button>
                                  <button type="button" className="item-wise-confirm-primary" disabled={busy || loading || !!error || staleConfirmation || !strictlyEligible || typeof readyCallback !== "function"} onClick={() => confirmAction(readyCallback, confirmation.entries)}>
                                    {busy ? <><CircularProgress size={12} color="inherit" aria-label="Marking items ready" /> Marking ready…</> : "Confirm all ready"}
                                  </button>
                                </div>
                              )}
                              <div className="item-wise-quantity-control">
                                {needsVariant && <label>Variant to mark ready
                                  <select aria-label={`Variant to mark ready for ${group?.name}`} value={quantityVariants[draftKey] || ""}
                                    disabled={busy || loading || !!error}
                                    onChange={(event) => {
                                      cancelConfirmation();
                                      setQuantityVariants((current) => ({ ...current, [draftKey]: event.target.value }));
                                      setQuantityDrafts((current) => ({ ...current, [draftKey]: "" }));
                                    }}>
                                    <option value="">Choose variant</option>
                                    {variants.map(([id, label]) => <option key={id} value={id}>{label} ({id})</option>)}
                                  </select>
                                </label>}
                                <label htmlFor={`ready-quantity-${panelIndex}-${dishIndex}`}>Quantity to mark ready</label>
                                <div className="item-wise-quantity-row">
                                  <input id={`ready-quantity-${panelIndex}-${dishIndex}`} type="number" min="1" max={quantityLimit} step="1" inputMode="numeric" value={draft} disabled={busy || loading || !!error || (needsVariant && !quantityEntries.length)} onChange={(event) => setQuantityDrafts((current) => ({ ...current, [draftKey]: event.target.value }))} aria-describedby={`quantity-hint-${panelIndex}-${dishIndex}`} />
                                  <button type="button" className="item-wise-action item-wise-action--quantity" disabled={busy || loading || !!error || !validQuantity} onClick={() => setQuantityReview(groupPanel, parsedQuantity, panel.id, quantityEntries)}>Review quantity</button>
                                </div>
                                <span id={`quantity-hint-${panelIndex}-${dishIndex}`}>Whole portions, up to {formatNumber(quantityLimit)}.</span>
                              </div>
                              {quantityConfirm && (
                                <div className="item-wise-confirm item-wise-confirm--quantity" role="group" aria-label={`Review quantity for ${group?.name}`}>
                                  <p>Review: <strong>{group?.name}</strong> · {formatNumber(confirmation.quantity)} portions ready · {formatNumber(quantity - confirmation.quantity)} remain in processing.</p>
                                  <p className="item-wise-allocation-note">The kitchen API allocates the selected quantity across its orders.</p>
                                  {typeof quantityCallback !== "function" && <span className="item-wise-api-warning">Quantity-ready API not connected</span>}
                                  {actionError && <p className="item-wise-inline-error" role="alert">{actionError}</p>}
                                  <button type="button" onClick={cancelConfirmation} disabled={busy}>Cancel</button>
                                  <button type="button" className="item-wise-confirm-primary" disabled={busy || loading || !!error || staleConfirmation || typeof quantityCallback !== "function"} onClick={() => confirmAction(quantityCallback, confirmation.entries, [confirmation.quantity], "quantity")}>
                                    {busy ? <><CircularProgress size={12} color="inherit" aria-label="Marking items ready" /> Marking ready…</> : "Confirm quantity"}
                                  </button>
                                </div>
                              )}
                              {!strictlyEligible && <span className="item-wise-disabled-hint">{aggregatedReady
                                ? "Completion requires a variant for every Processing line."
                                : "Full completion requires task IDs and IN_PROGRESS order status for every displayed line."}</span>}
                              {typeof readyCallback !== "function" && <span className="item-wise-disabled-hint">Full-ready API not connected.</span>}
                            </div>
                          )}

                          <details className="item-wise-breakdown">
                            <summary><span>Order-by-order breakdown</span><span className="item-wise-breakdown-count">{formatNumber(entries.length)} {entries.length === 1 ? "line" : "lines"}{instructionCount ? ` · ${instructionCount} with instructions` : ""}</span></summary>
                            <div className="item-wise-entry-list">
                              {entries.map((entry, entryIndex) => {
                                const addons = Array.isArray(entry?.addons) ? entry.addons : [];
                                return (
                                  <div className="item-wise-entry" key={entryKey(entry) || `${entry?.orderId}-${entryIndex}`}>
                                    <div className="item-wise-entry-heading">
                                      <div className="item-wise-entry-order">
                                        <strong>{entry?.token ? `#${entry.token}` : entry?.orderId || "Order"}</strong>
                                        {entry?.token && entry?.orderId && <span className="item-wise-entry-order-id">{entry.orderId}</span>}
                                        {entry?.platform && <span className="item-wise-platform">{entry.platform}</span>}
                                      </div>
                                      <div className="item-wise-entry-meta">
                                        {entry?.station && <span>{entry.station}</span>}
                                        <span className={`item-wise-entry-status${panel.id === "processing" ? " is-processing" : ""}`}>{panel.id === "processing" ? "Processing" : "Pending"}</span>
                                        <strong>×{formatNumber(entry?.quantity)}</strong>
                                      </div>
                                    </div>
                                    {typeof onOpenOrder === "function" && (
                                      <button type="button" className="item-wise-open-order" onClick={() => onOpenOrder(entry.orderId)} disabled={!entry.orderId}>Open order</button>
                                    )}
                                    {(entry?.variant || entry?.note || addons.length > 0) && (
                                      <div className="item-wise-entry-details">
                                        {entry?.variant && <span className="item-wise-detail-chip">Variant: {entry.variant}</span>}
                                        {addons.map((addon, addonIndex) => <span className="item-wise-detail-chip" key={`${addon?.name}-${addonIndex}`}>{addon?.name || "Add-on"}{Number(addon?.quantity) > 1 && ` ×${formatNumber(addon.quantity)}`}</span>)}
                                        {entry?.note && <p className="item-wise-entry-note"><span>Note</span>{entry.note}</p>}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </details>
                        </TimedDishCard>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {confirmation?.type === "reject-dialog" && (
        <RejectOrderConfirmation
          reasonOptions={REJECTION_REASON_OPTIONS}
          title="Reject these dish portions?"
          warning={`Reject ${formatNumber(quantityOf(confirmation.entries))} portions across ${formatNumber(orderCountOf(confirmation.entries))} orders? Only this dish will be rejected; other dishes remain unchanged. This cannot be undone.`}
          selectedReason={rejectReason}
          customReason={customReason}
          onReasonChange={(value) => { setRejectReason(value); setRejectError(""); }}
          onCustomReasonChange={(value) => { setCustomReason(value); setRejectError(""); }}
          error={rejectError || (typeof onReject !== "function" ? "Dish-level reject API is not connected. Nothing will be rejected until it is available." : rejectValidation.error)}
          isRejecting={busy}
          isReasonValid={typeof onReject === "function" && !rejectValidation.error && !loading && !error}
          onCancel={cancelConfirmation}
          onConfirm={() => {
            if (!allowReject || rejectValidation.error) {
              setRejectError(rejectValidation.error || "Rejection disabled by kitchen settings.");
              return;
            }
            confirmAction(onReject, confirmation.entries, [rejectValidation.reason]);
          }}
        />
      )}
    </section>
  );
};

export default ItemWiseView;
